/**
 * Lazy Loading Dropdown Hook
 *
 * This hook implements true lazy loading - data is only fetched when
 * the dropdown is opened for the first time, not on component mount.
 *
 * Benefits:
 * - Reduces initial page load time
 * - Saves bandwidth for unused dropdowns
 * - Better performance for pages with many dropdowns
 */
import { useState, useCallback, useRef } from 'react';
import { createApiClient, buildBackendUrl } from '@textile-erp/core';
const apiClient = createApiClient(buildBackendUrl('/api'));
/**
 * Hook for lazy loading dropdown options
 *
 * @example
 * ```typescript
 * const { options, loading, onMenuOpen } = useLazyDropdown({
 *   endpoint: '/parking/vehicle-types/dropdown/list',
 *   includeId: editingId
 * });
 *
 * <Select
 *   options={options}
 *   isLoading={loading}
 *   onMenuOpen={onMenuOpen}
 * />
 * ```
 */
export function useLazyDropdown({ endpoint, includeId, dependsOn, skipIfDependencyEmpty = true, }) {
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // Track if data has been fetched
    const hasFetchedRef = useRef(false);
    const isFetchingRef = useRef(false);
    const lastParamsRef = useRef('');
    const fetchOptions = useCallback(async (forceRefresh = false) => {
        // Skip if dependency is required but empty
        if (skipIfDependencyEmpty && dependsOn === '') {
            setOptions([]);
            return;
        }
        const currentParams = JSON.stringify({ endpoint, includeId, dependsOn });
        // Skip if already fetching or params haven't changed (unless force refresh)
        if (isFetchingRef.current)
            return;
        if (!forceRefresh && hasFetchedRef.current && currentParams === lastParamsRef.current)
            return;
        isFetchingRef.current = true;
        lastParamsRef.current = currentParams;
        setLoading(true);
        setError(null);
        try {
            // Build URL with parameters
            const params = new URLSearchParams();
            if (includeId)
                params.append('includeId', includeId);
            if (dependsOn)
                params.append('dependsOn', dependsOn);
            const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
            const response = await apiClient.get(url);
            setOptions(response.data || []);
            hasFetchedRef.current = true;
        }
        catch (err) {
            console.error('Failed to fetch dropdown options:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch options');
            setOptions([]);
        }
        finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    }, [endpoint, includeId, dependsOn, skipIfDependencyEmpty]);
    // Called when dropdown menu opens - triggers first fetch
    const onMenuOpen = useCallback(() => {
        if (!hasFetchedRef.current) {
            fetchOptions();
        }
    }, [fetchOptions]);
    // Refresh function for manual reload
    const refresh = useCallback(async () => {
        await fetchOptions(true);
    }, [fetchOptions]);
    return {
        options,
        loading,
        error,
        refresh,
        onMenuOpen,
        hasFetched: hasFetchedRef.current,
    };
}
